import { AlertTriangle, Clock, Zap, CheckCircle, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";

interface TrialBannerProps {
  plan: string;
  status: string;
  daysRemaining: number | null;
  isTrialExpired: boolean;
  splitsThisMonth: number;
  splitLimit: number | null;
  onUpgradeClick?: () => void;
  onExtendTrialClick?: () => void;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function TrialBanner({
  plan,
  status,
  daysRemaining,
  isTrialExpired,
  splitsThisMonth,
  splitLimit,
  onUpgradeClick,
  onExtendTrialClick,
}: TrialBannerProps) {
  // Active paid plans don't need a banner (unless near limit)
  if (status === 'active' && plan !== 'trial' && !plan?.includes('trial')) {
    if (splitLimit !== null && splitsThisMonth >= splitLimit * 0.8) {
      return (
        <div className="bg-yellow-500/10 border-yellow-500/20 border rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="font-medium text-yellow-700">Approaching Split Limit</p>
                <p className="text-sm text-yellow-600">
                  You've used {splitsThisMonth} of {splitLimit} splits this month.
                </p>
              </div>
            </div>
            <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700" onClick={onUpgradeClick}>
              Upgrade Plan
            </Button>
          </div>
        </div>
      );
    }
    return null;
  }

  // Trial expired
  if (isTrialExpired || status === 'expired') {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-destructive/20 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="font-medium text-destructive">Trial Expired</p>
              <p className="text-sm text-destructive/80">
                Your free trial has ended. Upgrade or request an extension to continue.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {onExtendTrialClick && (
              <Button size="sm" variant="outline" onClick={onExtendTrialClick}>
                <Gift className="h-4 w-4 mr-2" />
                Extend
              </Button>
            )}
            <Button size="sm" variant="destructive" onClick={onUpgradeClick}>
              Choose a Plan
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Active trial with days remaining
  if (status === 'trialing' && daysRemaining !== null) {
    const urgency = daysRemaining <= 3 ? 'urgent' : daysRemaining <= 7 ? 'warning' : 'info';
    
    const colors = {
      urgent: {
        bg: 'bg-orange-500/10',
        border: 'border-orange-500/20',
        iconBg: 'bg-orange-500/20',
        icon: 'text-orange-600',
        text: 'text-orange-700',
        subtext: 'text-orange-600',
      },
      warning: {
        bg: 'bg-yellow-500/10',
        border: 'border-yellow-500/20',
        iconBg: 'bg-yellow-500/20',
        icon: 'text-yellow-600',
        text: 'text-yellow-700',
        subtext: 'text-yellow-600',
      },
      info: {
        bg: 'bg-primary/10',
        border: 'border-primary/20',
        iconBg: 'bg-primary/20',
        icon: 'text-primary',
        text: 'text-primary',
        subtext: 'text-primary/80',
      },
    };

    const c = colors[urgency];

    return (
      <div className={`${c.bg} border ${c.border} rounded-lg p-4 mb-6`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${c.iconBg} rounded-lg`}>
              <Clock className={`h-5 w-5 ${c.icon}`} />
            </div>
            <div>
              <p className={`font-medium ${c.text}`}>
                {daysRemaining === 1 ? '1 day' : `${daysRemaining} days`} left in your trial
              </p>
              <p className={`text-sm ${c.subtext}`}>
                {splitLimit !== null 
                  ? `${splitsThisMonth}/${splitLimit} splits used • ` 
                  : ''}
                {daysRemaining <= 7 && onExtendTrialClick 
                  ? 'Need more time? Request an extension!' 
                  : '100 free splits with 0% commission during your intro trial'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {daysRemaining <= 7 && onExtendTrialClick && (
              <Button size="sm" variant="outline" onClick={onExtendTrialClick}>
                <Gift className="h-4 w-4 mr-2" />
                Extend
              </Button>
            )}
            <Button size="sm" onClick={onUpgradeClick}>
              <Zap className="h-4 w-4 mr-2" />
              View Plans
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Pending activation
  if (status === 'pending') {
    return (
      <div className="bg-blue-500/10 border-blue-500/20 border rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <CheckCircle className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-blue-700">Account Pending Activation</p>
            <p className="text-sm text-blue-600">
              Your account is being set up. Contact support if this takes too long.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
